import { OrderDetail } from '@interfaces/OrderTypes';
import { generatePdf, generateDoublePdf, isPdfMakeAvailable } from '@utils/printUtils';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { appStore } from '@store/index';
import { useDetailedOrders } from './orders';
import { capturePrintError, reportTicketResidual } from '@utils/print/printSentry';
import { openPdfTab } from '@utils/print/openPdfTab';
import { getPlatform } from '@utils/analytics/platform';
import { noteExpectedFailure } from '@utils/observability/expectedFailure';

interface PrintOrderProps {
  orderId: string;
  detailedOrder?: OrderDetail;
}

export const usePrintOrder = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { getOrder } = useDetailedOrders();

  const printOrder = async ({ orderId, detailedOrder }: PrintOrderProps) => {
    // Synchronous guard, BEFORE opening anything: if the CDN global never loaded there is no PDF
    // to put in a tab, and leaving one open would be an orphan about:blank.
    if (!isPdfMakeAvailable()) {
      capturePrintError(new Error('pdfMake global no disponible (el script del CDN no cargó)'), {
        tag: 'web_print_pdfmake_missing',
        trigger: 'web',
        extra: { orderId },
      });
      toast.error('No se pudo cargar el generador de PDF. Recarga la página e inténtalo de nuevo.');
      return;
    }

    // Opened HERE, in the same tick as the click, and not after the awaits below: see openPdfTab.
    const pdfTab = openPdfTab();
    // Declaredly defensive, and deliberately narrow: it only distinguishes a SYNCHRONOUS throw on
    // the rescue path from any other web print failure. pdfmake 0.2.7 cannot throw synchronously
    // out of `download()`, so the download failing does NOT land here — see the note next to
    // `web_print_popup_denied` in printSentry.ts for where that signal actually shows up.
    let usedDownloadFallback = false;

    try {
      setIsLoading(true);
      const commerce = appStore.getState().commerce;
      const response = detailedOrder || (await getOrder(orderId));

      // Aviso a Sentry si el desglose del ticket no cuadra con el total que se va a imprimir.
      reportTicketResidual(response, 'pdf');

      // Read ticket type from localStorage (for web printing)
      const ticketType = (localStorage.getItem('printTicketType') as 'normal' | 'double') || 'normal';

      const pdf =
        ticketType === 'double'
          ? await generateDoublePdf(
              commerce,
              response.pedido,
              response.productos,
              commerce?.config_form_entrega?.referencias?.label || 'Referencias',
            )
          : await generatePdf(
              commerce,
              response.pedido,
              response.productos,
              commerce?.config_form_entrega?.referencias?.label || 'Referencias',
            );

      if (pdfTab) {
        // Fresh literal every time: pdfmake MUTATES the options object (it sets autoPrint),
        // so a shared or frozen constant would break on the second print.
        pdf.print({}, pdfTab);
        return;
      }

      // Popups denied: the merchant gets the ticket as a file instead of a click that does
      // nothing. Breadcrumb, NOT captureException — a fallback that works is not an incident,
      // and even a `warning` would keep the issue open and keep counting events.
      //
      // Emitted BEFORE `download()` on purpose: the download's own failure is asynchronous and
      // escapes this try/catch, so this breadcrumb is the only thing that will identify the
      // untagged `unhandledrejection` it turns into. It states the INTENT, not the outcome —
      // at this point nothing has been written to disk yet.
      usedDownloadFallback = true;
      noteExpectedFailure('print', 'pdf popup denied: falling back to file download', {
        orderId,
        platform: getPlatform(),
      });
      pdf.download(`pedido-${response?.pedido?.folio || orderId}.pdf`);
      toast.success('Tu navegador bloqueó la ventana del PDF: lo descargamos como archivo.');
    } catch (error) {
      // Never leak an orphan blank tab the merchant has to close by hand.
      pdfTab?.close();
      capturePrintError(error, {
        tag: usedDownloadFallback ? 'web_print_popup_denied' : 'web_print_failure',
        trigger: 'web',
        extra: { orderId },
      });
      // The old copy blamed the order ("o el pedido no existe") for a failure that was the PDF's:
      // it sent the merchant hunting for a nonexistent problem in an order that does exist.
      toast.error('No se pudo generar el ticket del pedido');
    } finally {
      setIsLoading(false);
    }
  };

  return { printOrder, isLoading };
};
